import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import { SearchNavButton } from "@/components/layout/SearchNavButton";
import { MessagesNavButton } from "@/components/social/MessagesNavButton";
import { NotificationBell } from "@/components/social/NotificationBell";
import { UserMenu } from "@/components/layout/UserMenu";
import useGroupsConfig from "@/hooks/useGroupsConfig";

const LINKS = [
  { to: "/chatter", label: "Chatter" },
  { to: "/people", label: "People" },
  { to: "/businesses", label: "Businesses" },
  { to: "/capital", label: "Capital" },
  // Groups is a primary destination (GRP-DEC-027). The item is rendered only when the
  // safe Groups config says this viewer can see the surface; every /api/groups route
  // 404s independently of the navigation.
  { to: "/groups", label: "Groups", groups: true },
  { to: "/events", label: "Events" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export const PublicNav = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { visible: groupsVisible } = useGroupsConfig();
  const links = LINKS.filter((l) => !l.groups || groupsVisible);

  return (
    <header data-so-chrome className="sticky top-0 z-40 bg-[hsl(var(--card)/0.72)] shadow-[0_4px_20px_-6px_rgba(15,23,42,0.15)] backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`public-nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${
                    isActive ? "text-primary" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <SearchNavButton />
          {user && <MessagesNavButton />}
          {user && <NotificationBell />}
          {user ? (
            <Button onClick={() => navigate("/dashboard")} data-testid="public-nav-dashboard-button">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="public-nav-login-link">
                Log in
              </Button>
              <Button onClick={() => navigate("/register")} data-testid="public-nav-signup-link">
                Create account
              </Button>
            </>
          )}
          {user && <UserMenu />}
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <SearchNavButton />
          {user && <MessagesNavButton />}
          {user && <NotificationBell />}
          {user && <UserMenu />}
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card"
            onClick={() => setOpen((v) => !v)}
            data-testid="public-nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-[hsl(var(--card)/0.85)] backdrop-blur-xl lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1 px-4 py-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2 text-sm font-[500] ${
                    isActive ? "bg-teal-50 text-primary" : "text-foreground/80 hover:bg-secondary"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              {user ? (
                <Button className="flex-1" onClick={() => navigate("/dashboard")}>Dashboard</Button>
              ) : (
                <>
                  <Button variant="secondary" className="flex-1" onClick={() => navigate("/login")}>Log in</Button>
                  <Button className="flex-1" onClick={() => navigate("/register")}>Create account</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
